import { useMemo } from 'react'
import { Box, Skeleton } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { generateUniqId } from 'src/model/Id'

export type RectangleDefinition = {
  width: string | number
  height: string | number
  color?: string
}

type SkeletonVariant =
  | 'text' | 'heading' | 'paragraph' | 'button' | 'icon' | 'chip'
  | 'input' | 'textarea' | 'select'
  | 'input-labeled' | 'textarea-labeled' | 'select-labeled'
  | 'avatar' | 'image' | 'card'

export type AppSkeletonProps = {
  count?: number
  direction?: 'row' | 'column'
  rects?: RectangleDefinition[]
  variant?: SkeletonVariant
  height?: number | string
  width?: number | string
}

const LABEL: RectangleDefinition = { width: '20%', height: 12 }

const VARIANTS: Record<SkeletonVariant, RectangleDefinition[]> = {
  // Basic
  text: [{ width: '100%', height: 16 }],
  heading: [{ width: '60%', height: 32 }],
  paragraph: [{ width: '100%', height: 60 }],
  button: [{ width: 100, height: 40 }],
  icon: [{ width: 24, height: 24 }],
  chip: [{ width: 80, height: 32 }],

  // Forms
  input: [{ width: '100%', height: 56 }],
  textarea: [{ width: '100%', height: 100 }],
  select: [{ width: '100%', height: 56 }],

  // Labeled Forms
  'input-labeled': [LABEL, { width: '100%', height: 56 }],
  'textarea-labeled': [LABEL, { width: '100%', height: 100 }],
  'select-labeled': [LABEL, { width: '100%', height: 56 }],

  // Media
  avatar: [{ width: 40, height: 40 }],
  image: [{ width: '100%', height: 150 }],
  card: [{ width: '100%', height: 200 }],
}

const applyOverrides = (
  rects: RectangleDefinition[],
  width?: number | string,
  height?: number | string,
): RectangleDefinition[] => {
  if (!width && !height) {
    return rects
  }

  return rects.map((rect, i) => ({
    ...rect,
    ...(width && { width }),
    ...(height && i === rects.length - 1 && { height }),
  }))
}

export const AppSkeleton = observer(({
  count = 1,
  direction = 'row',
  rects,
  variant,
  height,
  width,
}: AppSkeletonProps) => {
  const finalRects = useMemo(() => {
    if (rects?.length) {
      return rects
    }
    if (variant && VARIANTS[variant]) {
      return applyOverrides(VARIANTS[variant], width, height)
    }
    if (height || width) {
      return [{ width: width ?? '100%', height: height ?? 40 }]
    }

    return [{ width: '100%', height: 40 }]
  }, [variant, rects, height, width])

  const items = useMemo(() => Array.from({ length: count }, generateUniqId), [count])
  const rectKeys = useMemo(() => finalRects.map(generateUniqId), [finalRects])
  const isRow = direction === 'row'

  return (
    <Box display="flex" flexDirection={direction} gap={10} flexWrap="wrap" width="100%">
      {items.map(itemId => (
        <Box key={itemId} display="flex" flexDirection="column" gap={5} flex={isRow ? 1 : undefined}>
          {finalRects.map((rect, i) => (
            <Skeleton
              key={rectKeys[i]}
              variant="rectangular"
              width={rect.width}
              height={rect.height}
              sx={{ bgcolor: rect.color }}
            />
          ))}
        </Box>
      ))}
    </Box>
  )
})